import React, { useState, useEffect } from 'react';
import { MessageCircle, User, Phone, RefreshCw, X, CheckCircle, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { leadsAPI } from '@/lib/api';
import { toast } from 'sonner';
import AnimatedSection from './AnimatedSection';
import { useAppData } from '@/context/AppDataContext';

const LeadCaptureSection = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(null);
  
  // Usa o contexto global para settings em vez de fazer fetch próprio
  const { settings } = useAppData();

  const interestOptions = [
    { id: 'eletronicos', label: 'Eletronicos' },
    { id: 'casa', label: 'Casa & Cozinha' },
    { id: 'moda', label: 'Moda & Acessorios' },
  ];

  const toggleInterest = (id) => {
    setInterests((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  useEffect(() => {
    if (showModal && settings?.auto_redirect_enabled && countdown === null) {
      setCountdown(settings.auto_redirect_delay || 10);
    }
  }, [showModal, settings, countdown]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && settings?.whatsapp_group_link) {
      window.open(settings.whatsapp_group_link, '_blank');
    }
  }, [countdown, settings]);

  const GROUP_LINK = settings?.whatsapp_group_link || 'https://whatsapp.com/channel/0029Vb7oBZaKbYMPWBZkWb2i';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      await leadsAPI.create({
        name: name.trim(),
        phone: phone.trim(),
        source: 'homepage',
        interests,
      });
      setLoading(false);
      setShowModal(true);
      setCountdown(null);
      setName('');
      setPhone('');
    } catch (error) {
      console.error('Erro ao enviar lead:', error);
      setLoading(false);
      toast.error('Erro ao cadastrar. Tente novamente!');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(GROUP_LINK);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copiado!');
  };

  const handleOpenWhatsApp = () => {
    window.open(GROUP_LINK, '_blank');
  };

  return (
    <AnimatedSection>
      <section
        className="py-12 md:py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden"
        data-testid="lead-capture-section"
      >
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-80 h-80 bg-green-500/20 rounded-full blur-[100px]" />
        </div>

        <div className="container mx-auto px-4 max-w-xl relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-green-500/20 px-3 py-1.5 rounded-full mb-4">
              <MessageCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Gratis e sem spam</span>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
              Receba ofertas no <span className="text-green-400">WhatsApp</span>
            </h2>
            <p className="text-green-300 text-sm font-medium mb-1">
              Nosso grupo VIP envia as melhores ofertas todos os dias
            </p>
            <p className="text-slate-400 text-sm">Promocoes exclusivas direto no seu celular</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                className="h-12 pl-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-xl focus:border-green-500"
                data-testid="lead-name-input"
              />
            </div>

            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(00) 00000-0000"
                className="h-12 pl-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 rounded-xl focus:border-green-500"
                data-testid="lead-phone-input"
              />
            </div>

            {/* Interest Checkboxes */}
            <div className="flex flex-wrap gap-2">
              {interestOptions.map((opt) => (
                <label
                  key={opt.id}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm transition-all border ${
                    interests.includes(opt.id)
                      ? 'bg-green-500/20 border-green-500/50 text-green-300'
                      : 'bg-white/5 border-white/10 text-slate-400 hover:border-white/30'
                  }`}
                  data-testid={`interest-${opt.id}`}
                >
                  <input
                    type="checkbox"
                    checked={interests.includes(opt.id)}
                    onChange={() => toggleInterest(opt.id)}
                    className="sr-only"
                  />
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      interests.includes(opt.id) ? 'bg-green-500 border-green-500' : 'border-slate-500'
                    }`}
                  >
                    {interests.includes(opt.id) && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  {opt.label}
                </label>
              ))}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl shadow-lg shadow-green-500/30 animate-pulse-slow ring-2 ring-green-400/30 ring-offset-2 ring-offset-slate-900"
              data-testid="lead-submit-btn"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Processando...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  <span>QUERO RECEBER OFERTAS</span>
                </div>
              )}
            </Button>
          </form>
        </div>

        {/* Modal de Sucesso */}
        {showModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
            <div className="bg-gradient-to-br from-white to-slate-50 rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-scale-in">
              <button
                onClick={() => {
                  setShowModal(false);
                  setCountdown(null);
                }}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="modal-close-btn"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/50">
                  <CheckCircle className="w-14 h-14 text-white" />
                </div>
                <h3 className="font-heading text-3xl font-bold text-slate-900 mb-3">Parabens!</h3>
                <p className="text-slate-700 text-lg leading-relaxed">
                  <span className="font-semibold text-green-600">Voce esta quase la!</span>
                  <br />
                  Clique abaixo para entrar no nosso <span className="font-bold">Grupo VIP</span> e nao perder
                  nenhuma <span className="font-bold text-orange-600">oferta relampago</span>.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleOpenWhatsApp}
                  className="w-full h-16 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-lg rounded-xl shadow-lg shadow-green-500/50 animate-pulse-slow relative overflow-hidden"
                  data-testid="modal-whatsapp-btn"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                  <span className="relative flex items-center justify-center gap-2">
                    <MessageCircle className="w-6 h-6" />
                    ENTRAR NO GRUPO AGORA
                  </span>
                </Button>

                {settings?.auto_redirect_enabled && countdown !== null && countdown > 0 && (
                  <div className="text-center">
                    <p className="text-sm text-slate-600">
                      Redirecionando automaticamente em{' '}
                      <span className="font-bold text-green-600">{countdown}s</span>
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  className="w-full h-12 border-2 border-slate-300 hover:border-green-500 hover:bg-green-50 font-semibold rounded-xl"
                  data-testid="modal-copy-btn"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                      Link Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5 mr-2" />
                      Copiar Link do Grupo
                    </>
                  )}
                </Button>
              </div>

              <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                <p className="text-xs text-slate-500 mb-1 text-center">Link do grupo:</p>
                <p className="text-xs text-slate-700 font-mono break-all text-center">{GROUP_LINK}</p>
              </div>

              <div className="mt-6 flex items-center justify-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>100% Gratis</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Sem Spam</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Sair quando quiser</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </AnimatedSection>
  );
};

export default LeadCaptureSection;
